import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { selectUserId } from "fond/api";
import { Account } from "fond/types";
import { getItem, setItem } from "fond/utils/localStorage";

const PINNED_ACCOUNTS_KEY = "fond_pinned_accounts";

type UsePinnedReturn = {
  pinnedKey: string;
  pinned: string[];
  addToPinned: (account: Account) => void;
  removeFromPinned: (account: Account) => void;
  setPinnedOrder: (orderedIds: string[]) => void;
};

export const usePinned = (): UsePinnedReturn => {
  const userId = useSelector(selectUserId);
  const pinnedKey = `${PINNED_ACCOUNTS_KEY}_${userId}`;
  const { enqueueSnackbar } = useSnackbar();
  const [pinned, setPinned] = useState<string[]>(() => (userId ? getItem(pinnedKey, []) : []));

  // Re-seed pinned state when userId becomes available or changes
  useEffect(() => {
    setPinned(userId ? getItem(pinnedKey, []) : []);
  }, [pinnedKey, userId]);

  const addToPinned = useCallback(
    async (account: Account) => {
      if (!userId) return;
      const pinnedItems: string[] = getItem(pinnedKey, []);

      // Attempt to add to pinned only if it is not in pinned
      if (!pinnedItems.includes(account.ID)) {
        const newPinnedItems = [...pinnedItems, account.ID];
        setItem(pinnedKey, newPinnedItems);
        // Forces components to rerender with storage event update
        const newStorageEvent = new StorageEvent("storage", {
          key: pinnedKey,
          oldValue: JSON.stringify(pinnedItems),
          newValue: JSON.stringify(newPinnedItems),
        });
        window.dispatchEvent(newStorageEvent);

        enqueueSnackbar(`"${account.Name}" pinned`, {
          action: (
            <Button color="primary" onClick={() => removeFromPinned(account)}>
              Undo
            </Button>
          ),
        });
      }

      analytics.track("account.pinned", {
        accountId: account.ID,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinnedKey, enqueueSnackbar]
  );

  const removeFromPinned = useCallback(
    async (account: Account) => {
      if (!userId) return;
      const pinnedItems: string[] = getItem(pinnedKey, []);

      // Attempt to remove from pinned only if it is in pinned
      if (pinnedItems.includes(account.ID)) {
        const newPinnedItems = pinnedItems.filter((item) => item !== account.ID);
        setItem(pinnedKey, newPinnedItems);
        const newStorageEvent = new StorageEvent("storage", {
          key: pinnedKey,
          oldValue: JSON.stringify(pinnedItems),
          newValue: JSON.stringify(newPinnedItems),
        });
        window.dispatchEvent(newStorageEvent);

        enqueueSnackbar(`"${account.Name}" unpinned`, {
          action: (
            <Button color="primary" onClick={() => addToPinned(account)}>
              Undo
            </Button>
          ),
        });
      }

      analytics.track("account.unpinned", {
        accountId: account.ID,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinnedKey, enqueueSnackbar]
  );

  const setPinnedOrder = useCallback(
    (orderedIds: string[]) => {
      if (!userId) return;
      const currentPinned = getItem(pinnedKey, []);
      setItem(pinnedKey, orderedIds);
      setPinned(orderedIds); // Update React state to trigger re-render immediately

      // Dispatch custom event for same-tab updates
      const customEvent = new CustomEvent("pinnedAccountsChanged", { detail: { key: pinnedKey } });
      window.dispatchEvent(customEvent);

      // Dispatch storage event for cross-tab updates
      const newStorageEvent = new StorageEvent("storage", {
        key: pinnedKey,
        oldValue: JSON.stringify(currentPinned),
        newValue: JSON.stringify(orderedIds),
      });
      window.dispatchEvent(newStorageEvent);
    },
    [pinnedKey, userId]
  );

  useEffect(() => {
    const detectAccountItemChanges = (event: Event) => {
      let key: string | null = null;

      if (event instanceof StorageEvent) {
        key = event.key;
      } else if (event instanceof CustomEvent) {
        key = event.detail?.key;
      }

      if (key === pinnedKey) {
        setPinned(getItem(pinnedKey, []));
      }
    };

    // Listen for both storage events (cross-tab) and custom events (same-tab)
    window.addEventListener("storage", detectAccountItemChanges);
    window.addEventListener("pinnedAccountsChanged", detectAccountItemChanges);
    return () => {
      window.removeEventListener("storage", detectAccountItemChanges);
      window.removeEventListener("pinnedAccountsChanged", detectAccountItemChanges);
    };
  }, [pinnedKey]);

  return { pinnedKey, pinned, addToPinned, removeFromPinned, setPinnedOrder };
};
